import React from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { provinces } from "@/components/FormLead";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { getLeadSourceValue } from "@/utils/leadSource";

const formSchema = z.object({
    categoria: z.string().min(1, { message: 'La categoria è obbligatoria.' }),
    ragioneSociale: z.string().min(1, { message: 'La ragione sociale è obbligatoria.' }),
    email: z.string().email({ message: 'Inserisci un indirizzo email valido.' }).min(1, { message: 'L\'email è obbligatoria.' }),
    telefono: z.string().min(10, { message: 'Il numero di telefono deve avere almeno 10 cifre.' }).min(1, { message: 'Il telefono è obbligatorio.' }),
    piva: z.string().min(1, { message: 'Il sito web è obbligatorio.' }),
    provincia: z.string().min(1, { message: 'La provincia è obbligatoria.' }),
    terms: z.boolean().refine(val => val, { message: 'Devi accettare i termini e le condizioni.' }),
});


export interface FromDiventaPartnerProps {
    backgroundImage: string,
    title: string
    description?: string
    motivation?: string
    type?: string
}


const FormDiventaPartner = ({ backgroundImage, title, description, motivation, type }: FromDiventaPartnerProps) => {

    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            categoria: '',
            ragioneSociale: '',
            email: '',
            telefono: '',
            piva: '',
            provincia: '',
            terms: false
        }
    });

    const onSubmit = async (data: any) => {
        try {
            setIsSubmitting(true); // Imposta lo stato di loading

            console.log({
                ...data,
            });

            const { ragioneSociale, email, categoria, telefono, provincia, piva } = data;

            const motivazione = `Richiesta diventa partner da parte di ${ragioneSociale} 
            email: ${email}
            telefono: ${telefono}
            provincia: ${provincia}
            partita iva: ${piva}
            categoria: ${categoria}
            `



            const body = {
                origine: motivazione ? motivation + ` ${ragioneSociale}` : `Diventa partner ${type ?? ''} ${ragioneSociale}`,
                argomento: 'PARTNER',
                nome: ragioneSociale ?? '',
                cognome: '',
                telefono: telefono ?? '',
                email: email ?? '',
                provincia: provincia ?? '',
                citta: provincia ? provinces.find((p) => p.code === provincia)?.name : '',
                motivazione: categoria ?? '',
                fonte: getLeadSourceValue(),
            };

            console.log('Invio dei dati al server:', body);

            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                throw new Error('Errore nella richiesta: ' + response.status);
            }

            const result = await response.json();
            console.log('Risultato della richiesta:', result);

            setIsSubmitting(false); // Disabilita lo stato di loading

            if (result.status === 'success') {
                form.reset();
                const descrizione = 'Grazie per averci contattato. Ti risponderemo al più presto.';
                await router.push(`/thank-you-page/diventa-partner?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(ragioneSociale)}&telefono=${encodeURIComponent(telefono)}`);
            } else {
                console.error('Errore durante l\'invio dei dati:', result);
            }
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
            setIsSubmitting(false); // Disabilita lo stato di loading in caso di errore
        }
    };


    return (
        <>
            <section
                className="h-[calc(100svh-100px)] max-sm:h-full max-sm:py-20 text-white"
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                }}
            >
                <div className="h-full flex items-center">

                    <div className="flex flex-col max-w-xl text-center relative mx-auto max-sm:px-6 max-sm:my-8">
                        <h2 className="text-3xl max-sm:text-xl top-[-20px] relative font-bold mb-3">{title}</h2>
                        {description &&
                            <p className="text-xl max-sm:text-xl top-[-20px] relative font-light mb-3">{description}</p>}
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}
                                className="flex flex-col items-center space-y-8 max-sm:mt-[80px]">
                                <FormField control={form.control} name="categoria"
                                    render={({ field, fieldState }) => (
                                        <FormItem className="w-full">
                                            <FormControl>
                                                <Controller
                                                    control={form.control}
                                                    name="categoria"
                                                    render={({ field }) => (
                                                        <Select {...field}
                                                            onValueChange={(value) => field.onChange(value)}
                                                            value={field.value}>
                                                            <SelectTrigger
                                                                className="opacity-30 text-black">
                                                                <SelectValue
                                                                    placeholder="Seleziona una categoria" />

                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="Commercianti">Rivenditori
                                                                    Plurimarche</SelectItem>
                                                                <SelectItem value="Concessionari">Concessionari
                                                                    Ufficiali</SelectItem>
                                                                <SelectItem value="Società di noleggio">Società
                                                                    di
                                                                    Noleggio</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            <FormMessage>{fieldState.error?.message}</FormMessage>
                                        </FormItem>
                                    )} />
                                <FormField control={form.control} name="ragioneSociale" render={({ field, fieldState }) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input {...field} className="opacity-30 text-black" placeholder="La Ragione Sociale dell'Azienda" />
                                        </FormControl>
                                        <FormMessage>{fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="email" render={({ field, fieldState }) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input {...field} className="opacity-30 text-black"
                                                placeholder="L'email dell'Azienda" />
                                        </FormControl>
                                        <FormMessage>{fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="telefono" render={({ field, fieldState }) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input {...field} className="opacity-30 text-black"
                                                placeholder="Il telefono dell'Azienda" />
                                        </FormControl>
                                        <FormMessage>{fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="piva" render={({ field, fieldState }) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input {...field} className="opacity-30 text-black"
                                                placeholder="La P. IVA dell'Azienda" />
                                        </FormControl>
                                        <FormMessage>{fieldState.error?.message}</FormMessage>
                                    </FormItem>
                                )} />
                                <FormField control={form.control} name="provincia"
                                    render={({ field, fieldState }) => (
                                        <FormItem className="w-full">
                                            <FormControl>
                                                <Controller
                                                    control={form.control}
                                                    name="provincia"
                                                    render={({ field }) => (
                                                        <Select {...field}
                                                            onValueChange={(value) => field.onChange(value)}
                                                            value={field.value}>
                                                            <SelectTrigger
                                                                className="opacity-30 text-black">
                                                                <SelectValue
                                                                    placeholder="Seleziona una provincia" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {provinces.map((province) => (
                                                                    <SelectItem
                                                                        key={province.code}
                                                                        value={province.code}>
                                                                        {province.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            <FormMessage>{fieldState.error?.message}</FormMessage>
                                        </FormItem>
                                    )} />
                                <div className="flex items-center">
                                    <FormField control={form.control} name="terms" render={({ field, fieldState }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Checkbox checked={field.value} className="text-white"
                                                    onCheckedChange={field.onChange} />
                                            </FormControl>
                                        </FormItem>
                                    )} />
                                    <label htmlFor="terms" className="ml-2 cursor-pointer">
                                        Accetto i <a href="#" className=" underline">termini e
                                            condizioni</a>.
                                    </label>
                                    {form.formState.errors.terms && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {form.formState.errors.terms.message}
                                        </div>
                                    )}
                                </div>
                                <Button className="bg-[#D73133] hover:bg-[#D73133]/90 w-[300px] text-white"
                                    type="submit">
                                    Invia
                                    <ChevronRight className="h-6 w-6 ml-2" />
                                </Button>
                            </form>
                        </Form>

                    </div>
                </div>
            </section>

        </>
    );
}

export default FormDiventaPartner;
